import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Mail, MessageSquare, Clock, ChevronDown } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import axios from 'axios';
import { usePageSeo } from '../lib/seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  usePageSeo('contact', { title: 'Contact Us', description: 'Questions about moving from Australia to Europe or about our guides? Get in touch with The Expat Studio.' });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        setIsSuccess(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
      }
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly.');
      console.error('Contact form error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fadeUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const faqs = [
    {
      question: 'Are your guides specific to Australians?',
      answer: 'Yes! Our guides are specifically written for Australians relocating overseas. We include Australian-specific information like exit requirements, tax implications, and embassy details. However, much of the destination country information is useful for anyone planning to relocate there.'
    },
    {
      question: 'How are the guides delivered?',
      answer: 'All our products are digital downloads delivered instantly via email after purchase. You\'ll receive PDF files that you can view on any device, print at home, or access from anywhere in the world.'
    },
    {
      question: 'Can I request a guide for a different country?',
      answer: 'Absolutely! We\'re always expanding our collection. If you\'d like to see a guide for a specific country, please let us know through this contact form. Countries with high demand will be prioritized in our development schedule.'
    },
    {
      question: 'What\'s your refund policy?',
      answer: 'We offer a 14-day money-back guarantee on all our products. If you\'re not satisfied with your purchase for any reason, simply email us within 14 days for a full refund. No questions asked.'
    },
    {
      question: 'How often are the guides updated?',
      answer: 'We review and update our guides regularly to ensure all information is current. When significant changes occur (like visa rule updates), we prioritize updates immediately. All customers receive free access to updated versions.'
    },
    {
      question: 'Do you offer custom consulting?',
      answer: 'Currently, we focus on providing comprehensive self-service guides rather than one-on-one consulting. However, if you have specific questions after purchasing our guides, feel free to reach out and we\'ll do our best to help.'
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Header */}
      <section data-testid="contact-header" className="container-custom mb-16">
        <motion.div {...fadeUp} className="text-center">
          <span className="inline-block text-[#C5A059] text-sm font-medium tracking-wider uppercase mb-4">
            Contact Us
          </span>
          <h1 className="font-serif text-4xl md:text-5xl font-semibold text-[#1E1622] mb-4">
            Get in Touch
          </h1>
          <p className="text-[#1E1622]/70 max-w-2xl mx-auto">
            Have a question about our guides or need help with your relocation planning? 
            We'd love to hear from you.
          </p>
        </motion.div>
      </section>

      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Form */}
          <motion.div {...fadeUp}>
            <div className="bg-white rounded-3xl border border-[#E6DAC5] p-8 md:p-10 shadow-sm">
              <h2 className="font-serif text-2xl font-semibold text-[#1E1622] mb-6">
                Send us a message
              </h2>

              {isSuccess ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  data-testid="contact-success"
                  className="bg-[#C5A059]/10 rounded-2xl p-8 text-center"
                >
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#C5A059]/20 flex items-center justify-center">
                    <MessageSquare size={28} className="text-[#C5A059]" />
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-[#1E1622] mb-2">
                    Message sent!
                  </h3>
                  <p className="text-[#1E1622]/70">
                    Thank you for reaching out. We'll get back to you within 24-48 hours.
                  </p>
                </motion.div>
              ) : (
                <form 
                  onSubmit={handleSubmit}
                  data-testid="contact-form"
                  className="space-y-6"
                >
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-[#1E1622] mb-2">
                        Name <span className="text-[#3B164C]">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        data-testid="contact-name-input"
                        placeholder="Your name"
                        required
                        className="input-editorial"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-[#1E1622] mb-2">
                        Email <span className="text-[#3B164C]">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        data-testid="contact-email-input"
                        placeholder="you@example.com"
                        required
                        className="input-editorial"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-[#1E1622] mb-2">
                      Subject <span className="text-[#3B164C]">*</span>
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      data-testid="contact-subject-input"
                      placeholder="What's this about?"
                      required
                      className="input-editorial"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-[#1E1622] mb-2">
                      Message <span className="text-[#3B164C]">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      data-testid="contact-message-input"
                      placeholder="Tell us more..."
                      required
                      rows={5}
                      className="input-editorial resize-none"
                    />
                  </div>

                  {error && (
                    <p data-testid="contact-error" className="text-red-500 text-sm">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit"
                    className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Message
                        <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="mt-8 grid sm:grid-cols-2 gap-6">
              <div className="flex items-start gap-4 p-6 bg-[#F5EFE9] rounded-2xl">
                <div className="w-10 h-10 rounded-full bg-[#3B164C]/10 flex items-center justify-center flex-shrink-0">
                  <Mail size={18} className="text-[#3B164C]" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#1E1622] mb-1">Email us</h4>
                  <a 
                    href="mailto:hello@theexpatstudio.com"
                    data-testid="contact-email-link"
                    className="text-sm text-[#3B164C] hover:underline"
                  >
                    hello@theexpatstudio.com
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4 p-6 bg-[#F5EFE9] rounded-2xl">
                <div className="w-10 h-10 rounded-full bg-[#3B164C]/10 flex items-center justify-center flex-shrink-0">
                  <Clock size={18} className="text-[#3B164C]" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#1E1622] mb-1">Response time</h4>
                  <p className="text-sm text-[#1E1622]/70">
                    Within 24-48 hours
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* FAQ Section */}
          <motion.div {...fadeUp}>
            <h2 className="font-serif text-2xl font-semibold text-[#1E1622] mb-6">
              Frequently Asked Questions
            </h2>
            
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`faq-${index}`}
                  data-testid={`faq-item-${index}`}
                  className="bg-white rounded-2xl border border-[#E6DAC5] px-6 overflow-hidden"
                >
                  <AccordionTrigger className="text-left font-medium text-[#1E1622] hover:text-[#3B164C] py-5 hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#1E1622]/70 pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
